import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, Download, Images, Loader2, Send, Star, Trash2, Upload, X } from "lucide-react";
import { getSavedPin } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 2 * 1024 * 1024; // must match the backend's chunk limit
const VISIBLE_THUMBS = 24; // large galleries paginate — "Show all" reveals the rest

const fmtMB = (bytes) => `${(Number(bytes || 0) / (1024 * 1024)).toFixed(1)} MB`;

// "The Gallery" — per-booking photo delivery in the owner inbox: chunked
// uploads (bypasses proxy body limits), a thumbnail strip with always-visible
// delete plus a select mode for clearing unused photos in bulk, and a
// one-tap email that hands the family their private gallery link.
export default function GalleryPanel({ booking, authMethod }) {
  const [files, setFiles] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(Boolean(booking?.emails?.gallery_sent));
  const [showAll, setShowAll] = useState(false);
  const [galleryToken, setGalleryToken] = useState("");
  // select mode: tap photos (touch-friendly) and clear unused ones in bulk
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [armed, setArmed] = useState(false); // two-step bulk delete confirm
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importNote, setImportNote] = useState("");
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  const zipInputRef = useRef(null);

  const authHeaders = authMethod === "pin" ? { "x-schedule-pin": getSavedPin() } : {};

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/gallery/${booking.id}/files`, { headers: authHeaders });
      setFiles(data.files || []);
      setGalleryToken(data.token || "");
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id]);

  const totalSize = useMemo(
    () => (files || []).reduce((sum, f) => sum + (Number(f.size) || 0), 0),
    [files]
  );

  const uploadOne = async (file, onPct) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const init = await axios.post(
      `${API}/gallery/${booking.id}/upload/init`,
      { filename: file.name, content_type: file.type || "image/jpeg", size: file.size, total_chunks: totalChunks },
      { headers: authHeaders }
    );
    const uploadId = init.data.upload_id;
    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      await axios.post(
        `${API}/gallery/${booking.id}/upload/chunk?upload_id=${uploadId}&index=${i}`,
        chunk,
        { headers: { ...authHeaders, "Content-Type": "application/octet-stream" }, maxBodyLength: Infinity }
      );
      onPct(Math.round(((i + 1) / totalChunks) * 100));
    }
    await axios.post(`${API}/gallery/${booking.id}/upload/complete?upload_id=${uploadId}`, {}, { headers: authHeaders });
  };

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length || uploading) return;
    setUploading(true);
    setErr("");
    setProgress(0);
    try {
      let done = 0;
      for (const f of picked) {
        await uploadOne(f, (pct) =>
          setProgress(Math.round(((done + pct / 100) / picked.length) * 100))
        );
        done += 1;
      }
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "The upload didn't go through — try again.");
    }
    setUploading(false);
    setProgress(0);
    e.target.value = "";
  };

  const removeFile = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${booking.id}/file/${id}`, { headers: authHeaders });
      setFiles((f) => (f || []).filter((x) => x.id !== id));
    } catch {
      setErr("Couldn't remove that photo — try again.");
    }
  };

  const toggleSelected = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const deleteSelected = async () => {
    if (!selected.length) return;
    if (!armed) {
      setArmed(true); // two-step confirm — this clears photos for good
      return;
    }
    setDeleting(true);
    setErr("");
    try {
      await axios.delete(`${API}/gallery/${booking.id}/files`, {
        headers: authHeaders,
        data: { file_ids: selected },
      });
      setFiles((f) => (f || []).filter((x) => !selected.includes(x.id)));
      setSelected([]);
      setArmed(false);
      setSelectMode(false);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't delete — try again.");
    }
    setDeleting(false);
  };

  // Import a whole archive of pictures (e.g. a Lightroom session export) —
  // uploaded in chunks, the server extracts every photo inside.
  const importZip = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file || importing || uploading) return;
    setImporting(true);
    setErr("");
    setImportNote("");
    setProgress(0);
    try {
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      const init = await axios.post(
        `${API}/gallery/${booking.id}/upload/init`,
        {
          filename: file.name,
          content_type: file.type || "application/zip",
          size: file.size,
          total_chunks: totalChunks,
          kind: "zip-import",
        },
        { headers: authHeaders }
      );
      const uploadId = init.data.upload_id;
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        await axios.post(
          `${API}/gallery/${booking.id}/upload/chunk?upload_id=${uploadId}&index=${i}`,
          chunk,
          { headers: { ...authHeaders, "Content-Type": "application/octet-stream" }, maxBodyLength: Infinity }
        );
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      const done = await axios.post(
        `${API}/gallery/${booking.id}/upload/complete?upload_id=${uploadId}`,
        {},
        { headers: authHeaders, timeout: 600000 }
      );
      setImportNote(`Imported ${done.data.imported} photo${done.data.imported === 1 ? "" : "s"}${done.data.skipped ? ` · ${done.data.skipped} non-image file${done.data.skipped === 1 ? "" : "s"} skipped` : ""}`);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "The import didn't go through — try again.");
    }
    setImporting(false);
    setProgress(0);
  };

  const sendGallery = async () => {
    if (!files || !files.length) return;
    setSending(true);
    setErr("");
    try {
      await axios.post(`${API}/gallery/${booking.id}/send`, {}, { headers: authHeaders });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't send — try again.");
    }
    setSending(false);
  };

  const toggleStar = async (f) => {
    try {
      const { data } = await axios.post(
        `${API}/gallery/${booking.id}/file/${f.id}/star`,
        {},
        { headers: authHeaders }
      );
      setFiles((list) =>
        (list || []).map((x) => (x.id === f.id ? { ...x, is_favorite: data.is_favorite } : x))
      );
    } catch {
      setErr("Couldn't update the star — try again.");
    }
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected([]);
    setArmed(false);
  };

  const thumbList = files ? (showAll ? files : files.slice(0, VISIBLE_THUMBS)) : [];

  return (
    <div className="mt-5 rounded-xl border border-white/10 bg-ink p-4" data-testid={`gallery-${booking.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-display text-lg tracking-wider text-white/85">
          THE <span className="text-accentlight">GALLERY</span>
          {loaded && files && (
            <span className="ml-2 font-body text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40" data-testid={`gallery-usage-${booking.id}`}>
              {files.length} photo{files.length === 1 ? "" : "s"} · {fmtMB(totalSize)}
            </span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={onPick}
            data-testid={`gallery-input-${booking.id}`}
          />
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            hidden
            onChange={importZip}
            data-testid={`gallery-zip-input-${booking.id}`}
          />
          <button
            type="button"
            onClick={() => zipInputRef.current?.click()}
            disabled={uploading || importing}
            data-testid={`gallery-import-${booking.id}`}
            title="Import a .zip archive that contains your pictures — every photo inside is added automatically"
            className="inline-flex items-center gap-2 rounded-full border border-amber-300/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-amber-200 transition-all hover:bg-amber-300/10 disabled:opacity-60"
          >
            {importing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Importing… {progress}%
              </>
            ) : (
              <>
                <Images className="h-3.5 w-3.5" /> Import ZIP
              </>
            )}
          </button>
          {files && files.length > 0 &&
            (selectMode ? (
              <button
                type="button"
                onClick={exitSelectMode}
                data-testid={`gallery-select-cancel-${booking.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white/70 transition-colors hover:text-white"
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setSelectMode(true)}
                data-testid={`gallery-select-${booking.id}`}
                title="Select unused photos and delete them to clear space"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white/70 transition-colors hover:border-red-400/60 hover:text-red-300"
              >
                <Trash2 className="h-3.5 w-3.5" /> Clear space
              </button>
            ))}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`gallery-add-${booking.id}`}
            className="inline-flex items-center gap-2 rounded-full border border-accentlight/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-accentlight transition-all hover:bg-accentlight/10 disabled:opacity-60"
          >
            {uploading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading… {progress}%
              </>
            ) : (
              <>
                <Upload className="h-3.5 w-3.5" /> Add photos
              </>
            )}
          </button>
        </div>
      </div>

      {(uploading || importing) && (
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-surface-hover" data-testid={`gallery-progress-${booking.id}`}>
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber-300 to-amber-400 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {importNote && !importing && (
        <p className="mt-3 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200" data-testid={`gallery-import-note-${booking.id}`}>
          {importNote}
        </p>
      )}

      {err && (
        <p className="mt-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid={`gallery-error-${booking.id}`}>
          {err}
        </p>
      )}

      {files && files.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {thumbList.map((f) => {
            const isPicked = selected.includes(f.id);
            return (
              <div
                key={f.id}
                className={`group relative h-20 w-20 overflow-hidden rounded-lg border transition-all ${isPicked ? "border-red-400 ring-2 ring-red-400/60" : "border-white/10"}`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${f.thumb_url || f.url}`}
                  alt={f.filename}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                {!selectMode && (
                  <button
                    type="button"
                    onClick={() => toggleStar(f)}
                    aria-label={f.is_favorite ? `Remove ${f.filename} from the Hall of Fame` : `Add ${f.filename} to the Hall of Fame`}
                    title={f.is_favorite ? "In the Hall of Fame — tap to remove" : "Star it — goes to your Hall of Fame"}
                    data-testid={`gallery-star-${f.id}`}
                    className={`absolute left-1 top-1 rounded-md p-1 transition-all duration-200 ${f.is_favorite ? "text-amber-300 drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]" : "bg-ink/85 text-white/60 hover:text-amber-300"}`}
                  >
                    <Star className={`h-3.5 w-3.5 ${f.is_favorite ? "fill-amber-300" : ""}`} />
                  </button>
                )}
                {selectMode ? (
                  <button
                    type="button"
                    onClick={() => toggleSelected(f.id)}
                    aria-label={isPicked ? `Unselect ${f.filename}` : `Select ${f.filename}`}
                    data-testid={`gallery-pick-${f.id}`}
                    className={`absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-md border text-[10px] font-bold transition-all ${isPicked ? "border-red-400 bg-red-500 text-white" : "border-white/50 bg-ink/85 text-white/80"}`}
                  >
                    {isPicked ? "✓" : ""}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => removeFile(f.id)}
                    aria-label={`Remove ${f.filename}`}
                    data-testid={`gallery-remove-${f.id}`}
                    title="Delete this photo"
                    className="absolute right-1 top-1 rounded-md bg-ink/85 p-1 text-white/70 transition-colors hover:text-red-300"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {files && files.length > VISIBLE_THUMBS && !showAll && !selectMode && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          data-testid="gallery-show-all"
          className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-accentlight transition-colors hover:text-white"
        >
          Show all {files.length} photos
        </button>
      )}

      {selectMode && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-red-400/20 bg-red-500/5 px-3 py-2.5" data-testid={`gallery-selectbar-${booking.id}`}>
          <p className="text-xs text-white/60">
            {selected.length} selected of {files.length} · {fmtMB((files || []).filter((f) => selected.includes(f.id)).reduce((s, f) => s + (Number(f.size) || 0), 0))}
          </p>
          <button
            type="button"
            onClick={() => setSelected((files || []).map((f) => f.id))}
            data-testid={`gallery-selectall-${booking.id}`}
            className="rounded-full border border-white/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white/70 transition-colors hover:text-white"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={deleteSelected}
            disabled={deleting || !selected.length}
            data-testid={`gallery-delete-${booking.id}`}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all disabled:cursor-not-allowed disabled:opacity-50 ${armed ? "bg-red-500 text-white" : "border border-red-400/50 text-red-300 hover:bg-red-500/20"}`}
          >
            {deleting ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" /> Deleting…
              </>
            ) : armed ? (
              `Tap again to delete ${selected.length} for good`
            ) : (
              <>
                <Trash2 className="h-3 w-3" /> Delete selected
              </>
            )}
          </button>
          <p className="text-[10px] text-white/30">Deleted photos come off the family's gallery immediately.</p>
        </div>
      )}

      {loaded && files && files.length === 0 && (
        <p className="mt-3 text-xs text-white/35">
          No photos yet — add the picks for this game and send them straight to the family.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={sendGallery}
          disabled={!files || !files.length || sending}
          data-testid={`gallery-send-${booking.id}`}
          title={`Sends a private gallery link to ${booking.email}`}
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {sending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending&hellip;
            </>
          ) : (
            <>
              <Send className="h-4 w-4" /> Send gallery email
            </>
          )}
        </button>
        {files && files.length > 0 && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/gallery/${booking.id}/zip?token=${encodeURIComponent(galleryToken)}`}
            download
            data-testid={`gallery-zip-${booking.id}`}
            className="inline-flex items-center gap-2 rounded-full border border-accentlight/40 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-accentlight transition-all hover:bg-accentlight/10"
          >
            <Download className="h-4 w-4" /> Download all
          </a>
        )}
        {sent && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-300"
            data-testid={`gallery-sent-${booking.id}`}
          >
            <CheckCircle2 className="h-3 w-3" /> Gallery emailed
          </span>
        )}
      </div>
    </div>
  );
}
